import { formatDateInstance } from "@/src/common/utils"
import {
  autoUpdate,
  flip,
  shift,
  useDismiss,
  useFloating,
  useInteractions
} from "@floating-ui/react"
import {
  addMonths,
  addYears,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths,
  subYears
} from "date-fns"
import React, { useState } from "react"

type ViewMode = "day" | "month" | "year"

type CustomCalendarProps = {
  handleChange?: (date: Date) => void
  selectedDate?: Date | null
  className?: string
  testId?: string
}

const CustomCalendar: React.FC<CustomCalendarProps> = ({
  handleChange,
  selectedDate,
  className,
  testId
}) => {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [viewMode, setViewMode] = useState<ViewMode>("day")
  const [isCalendarVisible, setIsCalendarVisible] = useState(false)
  const [value, setValue] = useState(
    selectedDate ? formatDateInstance(selectedDate, "MMM d, yyyy") : ""
  )

  const { refs, floatingStyles, context } = useFloating({
    placement: "bottom-start",
    middleware: [flip(), shift()],
    whileElementsMounted: autoUpdate,
    open: isCalendarVisible,
    onOpenChange: setIsCalendarVisible
  })

  const dismiss = useDismiss(context)
  const { getReferenceProps } = useInteractions([dismiss])

  /*
  Note: Maybe we can compile this custom calendar and deploy to a stears package manager. i.e. npm/yarn
    https://dev.to/vivekalhat/building-a-calendar-component-with-tailwind-and-date-fns-2c0i

  */

  const handleYearView = () => setViewMode("year")
  const handleMonthView = () => setViewMode("month")

  const toggleCalendar = () => setIsCalendarVisible((prev) => !prev)

  const handlePrev = () => {
    if (viewMode === "day") setCurrentDate(subMonths(currentDate, 1))
    if (viewMode === "year") setCurrentDate(subYears(currentDate, 12))
  }

  const handleNext = () => {
    if (viewMode === "day") setCurrentDate(addMonths(currentDate, 1))
    if (viewMode === "year") setCurrentDate(addYears(currentDate, 12))
  }

  const handleDateClick = (day: Date) => {
    setIsCalendarVisible(false)
    setValue(format(day, "MMM d, yyyy"))
    if (handleChange) {
      handleChange(day)
    }
  }

  const parseTypedDate = (text: string): Date | null => {
    const m = text.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
    if (!m) return null
    const month = Number(m[1]) - 1
    const day = Number(m[2])
    const year = Number(m[3])
    if (month < 0 || month > 11) return null
    const date = new Date(year, month, day)
    if (date.getMonth() !== month || date.getDate() !== day) return null
    return date
  }

  const commitTypedValue = () => {
    const parsed = parseTypedDate(value)
    if (parsed) {
      setValue(format(parsed, "MMM d, yyyy"))
      if (handleChange) {
        handleChange(parsed)
      }
    } else {
      setValue(
        selectedDate ? formatDateInstance(selectedDate, "MMM d, yyyy") : ""
      )
    }
  }

  const handleMonthClick = (month: number) => {
    setCurrentDate(new Date(currentDate.getFullYear(), month, 1))
    setViewMode("day")
  }

  const handleYearClick = (year: number) => {
    setCurrentDate(new Date(year, currentDate.getMonth(), 1))
    setViewMode("month")
  }

  const renderYears = () => {
    const years = []
    const startYear =
      currentDate.getFullYear() - (currentDate.getFullYear() % 4)

    for (let i = 0; i < 12; i++) {
      years.push(startYear + i)
    }

    const columns = []
    const itemsPerColumn = 4
    for (let i = 0; i < years.length; i += itemsPerColumn) {
      columns.push(years.slice(i, i + itemsPerColumn))
    }

    return (
      <div className="grid">
        {columns.map((column, colIndex) => (
          <div key={colIndex} className="grid-cols-4 gap-2 text-xs px-5 py-3">
            {column.map((year) => (
              <button
                key={year}
                onClick={() => handleYearClick(year)}
                className="text-center py-2 px-4 hover:bg-gray-600 dark:hover:bg-gray-800 text-white"
              >
                {year}
              </button>
            ))}
          </div>
        ))}
      </div>
    )
  }

  const renderMonths = () => {
    const months = Array.from({ length: 12 }, (_, i) =>
      format(new Date(2022, i), "MMM")
    )

    const columns = []
    const itemsPerColumn = 4
    for (let i = 0; i < months.length; i += itemsPerColumn) {
      columns.push(months.slice(i, i + itemsPerColumn))
    }

    return (
      <div className="grid">
        {columns.map((column, colIndex) => (
          <div key={colIndex} className="grid-cols-4 gap-2 px-5 py-3">
            {column.map((month, index) => (
              <button
                key={month}
                onClick={() =>
                  handleMonthClick(index + colIndex * itemsPerColumn)
                }
                className="hover:bg-gray-600 dark:hover:bg-gray-800 px-5 py-3 text-xs text-white"
              >
                {month}
              </button>
            ))}
          </div>
        ))}
      </div>
    )
  }

  const renderDays = () => {
    const monthStart = startOfMonth(currentDate)
    const monthEnd = endOfMonth(monthStart)
    const startDate = startOfWeek(monthStart)
    const endDate = endOfWeek(monthEnd)

    const days = eachDayOfInterval({ start: startDate, end: endDate })
    const dayHeaders = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]
    const dateFormat = "d"
    const rows: any = []

    let daysInRow: any = []

    rows.push(
      <div className="flex justify-between" key="day-headers">
        {dayHeaders.map((day, index) => (
          <div
            className="text-[#9CA3AF] cursor-default py-2.5 px-2.5"
            key={index}
          >
            {day}
          </div>
        ))}
      </div>
    )

    days.forEach((day, i) => {
      const formattedDate = format(day, dateFormat)
      const cloneDay = day

      if (selectedDate && isSameDay(day, selectedDate)) {
        daysInRow.push(
          <div
            className={`text-center cursor-pointer py-2.5 px-2.5 text-white ${
              !isSameMonth(day, monthStart)
                ? "text-[#9CA3AF]"
                : "dark:bg-[#1F2937] hover:bg-gray-600 text-white"
            }`}
            key={day.toString()}
            onClick={() => handleDateClick(cloneDay)}
            data-testid={testId}
          >
            {formattedDate}
          </div>
        )
      } else {
        daysInRow.push(
          <div
            className={`text-center cursor-pointer py-2.5 px-2.5 ${
              !isSameMonth(day, monthStart)
                ? "text-[#9CA3AF]"
                : "hover:bg-gray-600 dark:hover:bg-gray-800"
            }`}
            key={day.toString()}
            onClick={() => handleDateClick(cloneDay)}
          >
            {formattedDate}
          </div>
        )
      }

      if (daysInRow.length === 7) {
        rows.push(
          <div className="flex justify-between" key={i}>
            {daysInRow}
          </div>
        )
        daysInRow = []
      }
    })
    return <div className="text-xs px-5 text-white">{rows}</div>
  }

  return (
    <div ref={refs.setReference}>
      <div className={`relative ${className}`}>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commitTypedValue}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              commitTypedValue()
              e.currentTarget.blur()
            }
          }}
          placeholder="mm/dd/yyyy"
          {...getReferenceProps({
            onClick: toggleCalendar
          })}
          className="border-[0.5px] dark:border-[#4B5563] p-2 pr-10 w-full dark:bg-[#1F2937] focus:outline-none text-xs px-2.5 py-2 text-white"
          aria-label="Select Date"
          data-testid={testId}
        />
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer"
          {...getReferenceProps({
            onClick: toggleCalendar
          })}
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M9.75 1.51172H8.25V0.761719H7.5V1.51172H4.5V0.761719H3.75V1.51172H2.25C1.83579 1.51172 1.5 1.84751 1.5 2.26172V9.76172C1.5 10.1759 1.83579 10.5117 2.25 10.5117H9.75C10.1642 10.5117 10.5 10.1759 10.5 9.76172V2.26172C10.5 1.84751 10.1642 1.51172 9.75 1.51172ZM9.75 9.76172H2.25V4.51172H9.75V9.76172ZM9.75 3.76172H2.25V2.26172H3.75V3.01172H4.5V2.26172H7.5V3.01172H8.25V2.26172H9.75V3.76172Z"
            fill="#fff"
          />
        </svg>
      </div>
      {isCalendarVisible && (
        <div
          className="absolute z-10 border-[0.5px] dark:border-[#4B5563] bg-[#1A1D21] shadow-lg"
          role="dialog"
          aria-label="Calendar"
          ref={refs.setFloating}
          style={floatingStyles}
        >
          <div className="px-5 py-3 flex justify-between items-center mb-4 m-0 border-b-[0.5px] dark:border-[#4B5563]">
            <button onClick={handlePrev} aria-label="previous">
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M3.75 6L7.5 2.25L8.025 2.775L4.8 6L8.025 9.225L7.5 9.75L3.75 6Z"
                  fill="#fff"
                />
              </svg>
            </button>
            <div
              onClick={viewMode === "year" ? handleMonthView : handleYearView}
              className="text-xs font-normal text-white cursor-pointer"
            >
              {viewMode === "day" && format(currentDate, "MMMM yyyy")}
              {viewMode === "month" && format(currentDate, "yyyy")}
              {viewMode === "year" &&
                `${currentDate.getFullYear()} - ${
                  currentDate.getFullYear() + 11
                }`}
            </div>
            <button onClick={handleNext} aria-label="next">
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8.025 6L4.275 9.75L3.75 9.225L6.975 6L3.75 2.775L4.275 2.25L8.025 6Z"
                  fill="#fff"
                />
              </svg>
            </button>
          </div>
          {viewMode === "day" && renderDays()}
          {viewMode === "month" && renderMonths()}
          {viewMode === "year" && renderYears()}
        </div>
      )}
    </div>
  )
}

export default CustomCalendar
